import React from 'react'
import { IconProps } from './Icon.types'
import { StyledIcon } from './Icon.styles'
// Import specific icons instead of wildcard to avoid naming conflicts
import {
  Activity,
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Archive,
  ArrowDown as ArrowDownLine,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUp as ArrowUpLine,
  ArrowUpDown,
  Award,
  Banknote,
  BarChart,
  BarChart2,
  Bath,
  BatteryCharging,
  Bed,
  Bell,
  Bike,
  Bold,
  BookOpen,
  Bookmark,
  BookmarkX,
  Box,
  Briefcase,
  Building,
  Building2,
  Bus,
  Calculator,
  Calendar,
  CalendarCheck,
  Car,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  CircleAlert,
  CirclePlus,
  Clipboard,
  ClipboardCheck,
  Clock,
  Clock3,
  Cloud,
  CloudUpload,
  Code,
  Coffee,
  Coins,
  Compass,
  Copy,
  CreditCard,
  Crosshair,
  DollarSign,
  DoorOpen,
  Download,
  Droplet,
  Droplets,
  Edit,
  Edit2,
  Edit3,
  Eraser,
  ExternalLink,
  EyeOff,
  Eye,
  Factory,
  FileCheck,
  FileCode,
  FilePlus,
  FileText,
  Filter,
  Flame,
  Folder,
  FolderPlus,
  FolderTree,
  Footprints,
  Frame,
  Gauge,
  GitBranch,
  Globe,
  GraduationCap,
  Grid3x3,
  GripHorizontal,
  GripVertical,
  Hammer,
  HardHat,
  Heading1,
  Heading2,
  Heading3,
  Heart,
  HelpCircle,
  Hexagon,
  Highlighter,
  Home,
  Hospital,
  Image,
  Info,
  Italic,
  Key,
  Landmark,
  LandPlot,
  Layers,
  Layers2,
  Layers3,
  Leaf,
  Lightbulb,
  Link,
  List,
  ListChecks,
  ListOrdered,
  Loader,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Map,
  MapPin,
  MapPlus,
  Maximize,
  Menu,
  MessageCircle,
  Mic,
  Minus,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Mountain,
  MoveVertical,
  Package,
  Palette,
  PanelRightClose,
  Paperclip,
  ParkingCircle,
  Pause,
  Percent,
  Pentagon,
  PersonStanding,
  Phone,
  PieChart,
  Play,
  Plug,
  Plus,
  PlusCircle,
  Power,
  Quote,
  RailSymbol,
  Receipt,
  Redo,
  RefreshCw,
  RotateCw,
  Route,
  Ruler,
  Save,
  School,
  Search,
  Send,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Smile,
  Sofa,
  Sparkles,
  Square,
  SquareCheck,
  Star,
  StickyNote,
  Store,
  Strikethrough,
  Sun,
  SunMedium,
  Table,
  Tag,
  Target,
  Theater,
  ThumbsDown,
  ThumbsUp,
  Train,
  TramFront,
  Trash2,
  TreePine,
  Trees,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Type,
  Underline,
  Undo,
  Upload,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  Utensils,
  UtensilsCrossed,
  Wallet,
  Warehouse,
  Waves,
  Wifi,
  Wind,
  Wrench,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCcwSquare,
  LayoutDashboard,
  Maximize2,
  Languages,
  Printer,
  NotebookPen,
  FolderOpen,
  SquareActivity,
  SquareKanban,
  Group,
  Plane,
  Accessibility,
  Wheat,
  ArrowLeftRightIcon,
  Pencil,
  BarChart3,
  Battery,
  CornerDownLeft,
  Cpu,
  GitMerge,
  Layout,
  Thermometer,
  Volume2,
  VolumeX,
  Wine,
  ZapOff,
  TrainFront,
} from 'lucide-react'

// Map custom icon names to Lucide icons
const iconMap = {
  search: Search,
  menu: Menu,
  close: X,
  'arrow-right': ChevronRight,
  'arrow-left': ChevronLeft,
  'arrow-up': ChevronUp,
  'arrow-down': ChevronDown,
  check: Check,
  x: X,
  plus: Plus,
  minus: Minus,
  edit: Edit,
  delete: Trash2,
  home: Home,
  user: User,
  settings: Settings,
  'more-horizontal': MoreHorizontal,
  'more-vertical': MoreVertical,
  heart: Heart,
  star: Star,
  manual: SunMedium,
  eye: Eye,
  'eye-off': EyeOff,
  file: Paperclip,
  document: FileText,
  'document-text': FileText,
  'document-check': FileCheck,
  download: Download,
  image: Image,
  grid: Grid3x3,
  cash: Banknote,
  key: Key,
  cart: ShoppingCart,
  tag: Tag,
  clipboard: Clipboard,
  help: HelpCircle,
  info: Info,
  warning: TriangleAlert,
  error: CircleAlert,
  clock: Clock,
  // Building type icons
  building: Building,
  residential: Home,
  retail: Store,
  offices: Building2,
  industrial: Factory,
  public: Landmark,
  garage: Warehouse,
  other: HelpCircle,
  'not-classified': CirclePlus,
  office: Building2,
  'mixed-low': Layers2,
  'mix-high': Layers3,
  frame: Frame,
  // Building status icons
  protected: Shield,
  underground: ArrowDownToLine,
  'under-construction': HardHat,
  // Loading/Spinner icons
  loading: Loader2,
  spinner: Loader,
  refresh: RefreshCw,
  // Real estate icons - Keep custom icons as no good matches in lucide
  'land-plot': LandPlot,
  'real-estate-listing': null,
  'real-estate-transaction': ArrowLeftRightIcon,
  'building-permit': null,
  'listing-rental': null,
  'listing-purchase': null,
  // Text formatting icons
  'text-h-one': Heading1,
  'text-h-two': Heading2,
  'text-h-three': Heading3,
  'text-t': Type,
  'text-b': Bold,
  'text-italic': Italic,
  'text-underline': Underline,
  'text-strikethrough': Strikethrough,
  quotes: Quote,
  code: Code,
  'list-numbers': ListOrdered,
  'list-bullets': List,
  'text-align-left': AlignLeft,
  'text-align-center': AlignCenter,
  'text-align-right': AlignRight,
  'text-align-justify': AlignJustify,
  link: Link,
  eraser: Eraser,
  'arrow-u-up-left': Undo,
  'arrow-u-up-right': Redo,
  // New text formatting icons for RichTextEditor
  bold: Bold,
  italic: Italic,
  strikethrough: Strikethrough,
  highlighter: Highlighter,
  palette: Palette,
  'align-left': AlignLeft,
  'align-center': AlignCenter,
  'align-right': AlignRight,
  'align-justify': AlignJustify,
  'list-ordered': ListOrdered,
  'list-checks': ListChecks,
  // Action icons
  smile: Smile,
  undo: Undo,
  redo: Redo,
  copy: Copy,
  archive: Archive,
  trash: Trash2,
  paperclip: Paperclip,
  microphone: Mic,
  send: Send,
  stop: Square,
  play: Play,
  pause: Pause,
  // Missing icons for building layers
  map: Map,
  'map-plus': MapPlus,
  fire: Flame,
  droplet: Droplet,
  chart: TrendingUp,
  leaf: Leaf,
  sun: Sun,
  urbanization: Building,
  // Additional icons
  location: MapPin,
  users: Users,
  'chart-line': TrendingUp,
  calculator: Calculator,
  share: Share2,
  phone: Phone,
  mail: Mail,
  car: Car,
  briefcase: Briefcase,
  'credit-card': CreditCard,
  'log-out': LogOut,
  bell: Bell,
  layers: Layers,
  save: Save,
  reload: RotateCw,
  crosshair: Crosshair,
  compass: Compass,
  alert: TriangleAlert,
  filter: Filter,
  calendar: Calendar,
  'calendar-check': CalendarCheck,
  // Amenity icons
  bag: ShoppingBag,
  'shopping-cart': ShoppingCart,
  theater: Theater,
  utensils: Utensils,
  'graduation-cap': GraduationCap,
  hospital: Hospital,
  bus: Bus,
  ship: Ship,
  wrench: Wrench,
  store: Store,
  coffee: Coffee,
  restaurant: UtensilsCrossed,
  school: School,
  train: Train,
  bicycle: Bike,
  parking: ParkingCircle,
  'drag-handle': GripHorizontal,
  priority: TrendingUp,
  deal: Wallet,
  list: List,
  // Additional icons for features
  water: Droplets,
  tree: Trees,
  box: Package,
  shirt: Shirt,
  shield: Shield,
  activity: Activity,
  wind: Wind,
  wifi: Wifi,
  bed: Bed,
  polygon: Pentagon,
  // Missing icons from console errors
  folder: Folder,
  'file-text': FileText,
  table: Table,
  blueprint: FileCode,
  'chevron-right': ChevronRight,
  cube: Box,
  'map-pin': MapPin,
  ruler: Ruler,
  'check-circle': CheckCircle,
  coins: Coins,
  // New missing icons
  bookmark: Bookmark,
  'trash-2': Trash2,
  'refresh-cw': RefreshCw,
  'trending-up': TrendingUp,
  square: Square,
  'bar-chart-2': BarChart2,
  'dollar-sign': DollarSign,
  unbookmark: BookmarkX,
  // Transport and mobility icons
  tram: TramFront,
  highway: Route,
  'railway-track': RailSymbol,
  target: Target,
  'map-pin-filled': MapPin,
  zap: Zap,
  plug: Plug,
  'clock-3': Clock3,
  'world-map': Globe,
  walking: PersonStanding,
  // Missing icons - chevron variants
  'chevron-up': ChevronUp,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevrons-up': ChevronUp, // Added mapping for chevrons-up
  'chevrons-left': ChevronsLeft,
  'chevrons-right': ChevronsRight,
  // Missing icons - alerts and notifications
  'alert-circle': CircleAlert,
  'alert-triangle': TriangleAlert,
  // Missing icons - charts and data
  'bar-chart': BarChart,
  'pie-chart': PieChart,
  // Missing icons - ui controls
  sliders: SlidersHorizontal,
  maximize: Maximize,
  'panel-right-close': PanelRightClose,
  'grip-vertical': GripVertical,
  'plus-circle': PlusCircle,
  // Missing icons - objects and items
  award: Award,
  receipt: Receipt,
  'clipboard-check': ClipboardCheck,
  lock: Lock,
  power: Power,
  'battery-charging': BatteryCharging,
  gauge: Gauge,
  hexagon: Hexagon,
  // Missing icons - nature and environment
  cloud: Cloud,
  'cloud-upload': CloudUpload,
  flame: Flame,
  mountain: Mountain,
  'tree-pine': TreePine,
  footprints: Footprints,
  // Missing icons - transport and movement
  bike: Bike,
  upload: Upload,
  // Missing icons - buildings and structures
  'building-2': Building2,
  'hard-hat': HardHat,
  // Missing icons - user and people
  'user-check': UserCheck,
  'user-minus': UserMinus,
  'user-plus': UserPlus,
  // Missing icons - transport
  'arrow-left-right': ArrowLeftRight,
  'train-track': RailSymbol,
  route: Route,
  'tram-front': TramFront,
  // Missing icons - time of day
  moon: Moon,
  // Missing icons - text and notes
  note: StickyNote,
  'notebook-pen': NotebookPen,
  text: Type,
  // Missing icons - aliases for existing icons
  time: Clock,
  tool: Wrench,
  // Missing icons - maps
  'map-2': Map,
  // Missing icons - trends
  'trending-down': TrendingDown,
  // Missing icons - custom/project specific (use reasonable defaults)
  amount: DollarSign,
  'git-branch': GitBranch,
  'git-merge': GitMerge,
  'pipeline-type': GitBranch,
  pencil: Pencil,
  'bar-chart-3': BarChart3,
  battery: Battery,
  'corner-down-left': CornerDownLeft,
  cpu: Cpu,
  layout: Layout,
  thermometer: Thermometer,
  'volume-2': Volume2,
  'volume-x': VolumeX,
  wine: Wine,
  'zap-off': ZapOff,
  'train-front': TrainFront,
  area: LandPlot,
  land: LandPlot,
  // Missing icons - files and folders
  'book-open': BookOpen,
  'edit-2': Edit2,
  'edit-3': Edit3,
  'external-link': ExternalLink,
  'file-plus': FilePlus,
  'folder-plus': FolderPlus,
  'folder-tree': FolderTree,
  'folder-open': FolderOpen,
  'square-activity': SquareActivity,
  'square-kanban': SquareKanban,
  group: Group,
  lightbulb: Lightbulb,
  'move-vertical': MoveVertical,
  wallet: Wallet,
  // Additional missing icons
  loader: Loader,
  placement: MapPin,
  // Additional missing icons - UI controls and actions
  'arrows-left-right': ArrowLeftRight,
  'help-circle': HelpCircle,
  'share-2': Share2,
  // AI and enhancement icons
  sparkles: Sparkles,
  // Missing icons - navigation and controls
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
  'rotate-ccw': RotateCcw,
  'rotate-ccw-square': RotateCcwSquare,
  // Missing icons - interface elements
  printer: Printer,
  'layout-dashboard': LayoutDashboard,
  'maximize-2': Maximize2,
  languages: Languages,
  // Demographics and employment chart icons
  globe: Globe,
  plane: Plane,
  accessibility: Accessibility,
  wheat: Wheat,
  factory: Factory,
  landmark: Landmark,
  'check-square': SquareCheck,
  'shield-check': ShieldCheck,
  sofa: Sofa,
  // Property feature icons
  bath: Bath,
  trees: Trees,
  waves: Waves,
  warehouse: Warehouse,
  'arrow-up-down': ArrowUpDown,
  'arrow-up-line': ArrowUpLine,
  'arrow-down-line': ArrowDownLine,
  hammer: Hammer,
  'door-open': DoorOpen,
  // Help system icons
  messageCircle: MessageCircle,
  'message-circle': MessageCircle,
  thumbsUp: ThumbsUp,
  'thumbs-up': ThumbsUp,
  thumbsDown: ThumbsDown,
  'thumbs-down': ThumbsDown,
  // camelCase aliases for existing kebab-case icons
  helpCircle: HelpCircle,
  fileText: FileText,
  arrowLeft: ChevronLeft,
  chevronRight: ChevronRight,
  trendingUp: TrendingUp,
  alertTriangle: TriangleAlert,
  barChart2: BarChart2,
  chevronDown: ChevronDown,
  chevronUp: ChevronUp,
  chevronLeft: ChevronLeft,
  alertCircle: CircleAlert,
  dollarSign: DollarSign,
  mapPin: MapPin,
  pieChart: PieChart,
  percent: Percent,
  // Property Subtype Icons (custom SVG paths)
  'subtype-studio': null,
  'subtype-ground-floor-garden': null,
  'subtype-ground-floor': null,
  'subtype-floor': null,
  'subtype-duplex': null,
  'subtype-attic': null,
  'subtype-detached': null,
  'subtype-semi-detached': null,
  'subtype-terraced': null,
  'subtype-farmhouse': null,
} as const

// Custom SVG paths for icons that don't have good lucide equivalents
const customIconPaths = {
  'real-estate-listing':
    'M3 3v18h4V3m0 4h14v10c0 1.1-.9 2-2 2H9c-1.1 0-2-.9-2-2V7zm3 4l4-3 4 3v5h-8v-5zm2 3h4v2h-4z',
  'building-permit': 'M5 6h4v15H5V6zm2 2v2m0 3v2m0 3v2M9 6h12L9 11v-5zm10 5v6l-2 2m0 0h2m-2 0v2',
  'listing-rental':
    'M12 1a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM12 5L5 9m7-4l7 4M3 9h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z',
  'listing-purchase': 'M2 2h3v20H2V2zm3 3h17v2H5V5zm4 4v2m8-2v2M8 11h10v10H8V11z',
} as const

// Complex custom SVG elements for property subtypes (stroke-based like lucide icons)
const customIconElements: Record<string, React.ReactNode> = {
  // Apartment subtypes
  'subtype-studio': (
    <>
      <path d="M8 10h.01" />
      <path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      <rect x="4" y="2" width="16" height="20" rx="2" />
    </>
  ),
  'subtype-ground-floor-garden': (
    <>
      <path d="M14.5 19h.01" />
      <path d="M5.938 20.996v-3c0-.552.448-1 1-1h4c.552 0 1 .448 1 1v3" />
      <rect y="1.954" width="16" height="20" rx="2" x="0.998" />
      <path d="M21.085 20.334h1.766l-1.135-2.737h.757l-1.388-3.156-1.39 3.156h.759l-1.137 2.737h1.768zm0 0v1.683" />
    </>
  ),
  'subtype-ground-floor': (
    <>
      <path d="M17.3 19h.01" />
      <path d="M6.7 19h.01" />
      <path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      <rect x="4" y="2" width="16" height="20" rx="2" />
    </>
  ),
  'subtype-floor': (
    <>
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 10h.01" />
      <path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      <rect x="4" y="2" width="16" height="20" rx="2" />
    </>
  ),
  'subtype-duplex': (
    <>
      <path d="M12 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 10h.01" />
      <path d="M12 6h.01" />
      <path d="M16 6h.01" />
      <path d="M8 6h.01" />
      <path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      <rect x="4" y="2" width="16" height="20" rx="2" />
    </>
  ),
  'subtype-attic': (
    <>
      <path d="M14 4h.01" />
      <path d="M10 4h.01" />
      <rect y="6.998" width="16" height="16.002" rx="2" x="4" />
      <path d="M9 22v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
      <rect width="12" height="6" rx="2" x="6" y="1" />
    </>
  ),
  // House subtypes
  'subtype-detached': (
    <>
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  ),
  'subtype-semi-detached': (
    <>
      <path d="M9.009 21.019v-8c0-.552-.339-1-.755-1H5.233c-.417 0-.755.448-.755 1v8" />
      <path d="M1.572 11.021c0-.589.149-1.148.411-1.528l4.051-6c.431-.63 1.062-.63 1.494 0l4.05 6c.261.38.411.939.411 1.528v9c0 1.105-.518 2-1.158 2H2.729c-.638 0-1.157-.895-1.157-2v-9z" />
      <path d="M19.573 21.023v-8c0-.552-.339-1-.755-1h-3.021c-.417 0-.755.448-.755 1v8" />
      <path d="M12.136 11.025c0-.589.149-1.148.411-1.528l4.051-6c.431-.63 1.062-.63 1.494 0l4.05 6c.261.38.411.939.411 1.528v9c0 1.105-.518 2-1.158 2h-8.102c-.638 0-1.157-.895-1.157-2v-9z" />
    </>
  ),
  'subtype-terraced': (
    <>
      <path d="M1.254 11.032c0-.589.101-1.148.279-1.528l2.755-6c.293-.63.722-.63 1.016 0l2.754 6c.177.38.279.939.279 1.528v9c0 1.105-.352 2-.787 2H2.041c-.434 0-.787-.895-.787-2v-9z" />
      <path d="M8.342 11.049c0-.589.101-1.148.279-1.528l2.755-6c.293-.63.722-.63 1.016 0l2.754 6c.177.38.279.939.279 1.528v9c0 1.105-.352 2-.787 2H9.129c-.434 0-.787-.895-.787-2v-9z" />
      <path d="M15.509 11.038c0-.589.101-1.148.279-1.528l2.755-6c.293-.63.722-.63 1.016 0l2.754 6c.177.38.279.939.279 1.528v9c0 1.105-.352 2-.787 2h-5.509c-.434 0-.787-.895-.787-2v-9z" />
    </>
  ),
  'subtype-farmhouse': (
    <>
      <path d="M9.275 21v-7.073c0-.488-.319-.884-.712-.884H5.713c-.394 0-.713.396-.713.884V21" />
      <path d="M8.98 7.84c0-.131.09-.256.247-.341l2.443-1.341c.26-.141.641-.141.901 0l2.443 1.341c.157.086.247.21.247.341v2.011c0 .247-.312.447-.698.447H9.678c-.386 0-.698-.2-.698-.447V7.84z" />
      <path d="M1.052 10.885c0-.589.316-1.148.866-1.528l8.547-6c.91-.63 2.243-.63 3.153 0l8.547 6c.55.38.866.939.866 1.528v9c0 1.105-1.093 2-2.442 2H3.494c-1.349 0-2.442-.895-2.442-2v-9z" />
      <path d="M20.378 20.932v-6.867c0-.473-.539-.857-1.203-.857h-4.815c-.666 0-1.205.384-1.205.857v6.867" />
    </>
  ),
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 'medium',
  color,
  className,
  style,
  strokeWidth,
  fill,
}) => {
  const LucideIcon = iconMap[name as keyof typeof iconMap]
  const customPath = customIconPaths[name as keyof typeof customIconPaths]
  const customElement = customIconElements[name]

  // Handle complex custom SVG elements (stroke-based like lucide)
  if (customElement) {
    return (
      <StyledIcon
        $strokeWidth={strokeWidth}
        $size={size}
        $color={color}
        className={className}
        style={style}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={`${name} icon`}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth || 2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {customElement}
      </StyledIcon>
    )
  }

  // Handle custom icons that don't have good lucide equivalents
  if (customPath) {
    const isStrokeBased = [
      'real-estate-listing',
      'building-permit',
      'listing-rental',
      'listing-purchase',
    ].includes(name)

    return (
      <StyledIcon
        $strokeWidth={strokeWidth}
        $size={size}
        $color={color}
        className={className}
        style={style}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={`${name} icon`}
      >
        <path
          d={customPath}
          fill={isStrokeBased ? 'none' : 'currentColor'}
          stroke={isStrokeBased ? 'currentColor' : 'none'}
          strokeWidth={isStrokeBased ? strokeWidth || 2 : 0}
          strokeLinecap={isStrokeBased ? 'round' : undefined}
          strokeLinejoin={isStrokeBased ? 'round' : undefined}
        />
      </StyledIcon>
    )
  }

  if (!LucideIcon) {
    console.warn(`Icon "${name}" not found`) // TODO add back Thibault clement
    return null
  }

  // Calculate icon size
  let iconSize: number
  if (typeof size === 'number') {
    iconSize = size
  } else {
    const sizeMap = {
      xs: 12,
      small: 16,
      medium: 20,
      large: 24,
      xl: 32,
    }
    iconSize = sizeMap[size]
  }

  return (
    <StyledIcon
      $strokeWidth={strokeWidth}
      $size={size}
      $color={color}
      className={className}
      style={style}
      as="div"
    >
      <LucideIcon
        size={iconSize}
        color={color || 'currentColor'}
        strokeWidth={strokeWidth || 2}
        fill={fill || 'transparent'}
        role="img"
        aria-label={`${name} icon`}
        style={{ fill: fill || 'transparent' }}
      />
    </StyledIcon>
  )
}
